// Module tap-to-target/cycle state machine (design doc Section 4a/4b,
// LOCKED). Pure and deterministic given (state, now, ...) so it's
// unit-testable independent of any render loop, DOM, or wall clock.
//
// Countdowns are absolute target timestamps (`nextTickAt = now +
// duration`), per Section 16.30's load-bearing implementation rule --
// never a value that decrements on an interval, so a throttled/backgrounded
// tab can't cause drift.
//
// NOTE: the "cycle tap-bonus" mechanic originally specified here (tapping
// a completed cycle to restart it early) has been removed -- tapping a
// module is the *same* gesture used to assign/reassign/deactivate its
// target, and the two kept colliding: a tap meant to detach a module (or
// hand it to a new target) would instead silently restart its cycle
// whenever the module happened to be sitting "ready". See the design
// doc's Section 4a for the supersession note. A completed cycle now just
// rolls immediately into the next one, with no separate "ready" pause to
// tap into.

export type ModuleVisualState = "off" | "armed" | "cycling" | "oor" | "deactivating";

export interface WeaponCycleState {
  targetId: string | null;
  armed: boolean;
  nextTickAt: number | null;
  pausedRemainingMs: number | null;
}

export const IDLE_WEAPON: WeaponCycleState = {
  targetId: null,
  armed: false,
  nextTickAt: null,
  pausedRemainingMs: null
};

export interface CycleResult<T> {
  state: T;
  visual: ModuleVisualState;
  progress: number; // 0-1, meaningless when visual is "off"/"armed"
  justFired: boolean; // true on the exact tick a cycle completes
}

// Advances a weapon's cycle by one check. `inRange` is only meaningful
// when a target is assigned; pass anything when targetId is null.
export function tickWeaponCycle(state: WeaponCycleState, now: number, cycleDurationMs: number, inRange: boolean): CycleResult<WeaponCycleState> {
  if (!state.targetId) {
    return { state, visual: state.armed ? "armed" : "off", progress: 0, justFired: false };
  }

  if (!inRange) {
    const paused =
      state.nextTickAt !== null
        ? { ...state, nextTickAt: null, pausedRemainingMs: Math.max(0, state.nextTickAt - now) }
        : state;
    const progress = paused.pausedRemainingMs !== null ? 1 - paused.pausedRemainingMs / cycleDurationMs : 0;
    return { state: paused, visual: "oor", progress, justFired: false };
  }

  let next = state;
  if (next.pausedRemainingMs !== null) {
    next = { ...next, nextTickAt: now + next.pausedRemainingMs, pausedRemainingMs: null };
  }
  if (next.nextTickAt === null) {
    next = { ...next, nextTickAt: now + cycleDurationMs };
  }
  let justFired = false;
  if (now >= next.nextTickAt!) {
    next = { ...next, nextTickAt: now + cycleDurationMs };
    justFired = true;
  }
  return { state: next, visual: "cycling", progress: clamp01(1 - (next.nextTickAt! - now) / cycleDurationMs), justFired };
}

// Tap handler for a weapon circle (Section 4a's tap-module-then-target /
// target-then-module dual ordering). `selectedId` is whatever target the
// captain currently has selected elsewhere in the HUD, if any. Tapping a
// module that's already locked onto something always deactivates it --
// no special-cased "ready" behavior (see file header).
export function tapWeapon(state: WeaponCycleState, selectedId: string | null): WeaponCycleState {
  if (state.targetId) {
    return { ...state, targetId: null, armed: false, nextTickAt: null, pausedRemainingMs: null };
  }
  if (state.armed) {
    return { ...state, armed: false };
  }
  if (selectedId) {
    return { ...state, targetId: selectedId, armed: false };
  }
  return { ...state, armed: true };
}

// Called when a new target is selected elsewhere in the HUD (canvas tap,
// mini-map, etc.) -- an armed/pending weapon locks onto it immediately
// (Section 4a's "weapon-then-target" order).
export function lockArmedWeapon(state: WeaponCycleState, selectedId: string): WeaponCycleState {
  if (!state.armed) {
    return state;
  }
  return { ...state, armed: false, targetId: selectedId };
}

export interface SelfModuleCycleState {
  // "player", or a live fleet ship's own id (16.43) -- was a hardcoded
  // "player" | "escort" union back when there was only ever one other
  // ship to repair.
  targetId: string | null;
  active: boolean;
  nextTickAt: number | null;
  // True while finishing its current cycle before actually powering off
  // (Don's direction, item 4): deactivating mid-cycle no longer cuts the
  // repair short -- the cycle already in progress is left to complete
  // (grayed out via the "deactivating" visual below), and only THEN does
  // it actually go idle. Stops toggling on/off/on from healing faster
  // than the cycle timer allows. Re-tapping during this window cancels
  // the pending shutoff and resumes normally, same as "off" -> "on".
  deactivating: boolean;
}

export const IDLE_SELF_MODULE: SelfModuleCycleState = { targetId: null, active: false, nextTickAt: null, deactivating: false };

// Shield/Hull never go "oor" (Section 4b: they always target a ship, not
// a distant object) or "armed" (Section 4a: they activate immediately on
// tap, no pending state) -- only off / cycling / deactivating.
/**
 * Self-module tick, with an optional post-fire cooldown (16.101, Don's ruling).
 *
 * 16.100 measured the shield module as free, unlimited healing at exactly
 * `shieldRegenPerS` HP/s -- beating median incoming DPS by 1.9x at T1-T3 and up
 * to 7.5x at T4, which is why no fight ever felt dangerous. A cooldown rather
 * than a smaller per-cycle number was chosen deliberately: both fix the
 * arithmetic, but only the cooldown turns the module into a lump the captain
 * decides when to spend and then has to survive without.
 *
 * Modelled as a LONGER CYCLE rather than a separate lockout state (revised by
 * 16.103): the cycle takes `cycleDurationMs + cooldownMs` and restores
 * `cycleDurationMs` worth of regen at the end of it. That keeps the state machine
 * exactly as it was -- no new visual state -- and, crucially, means the progress
 * arc and the actual wait are the same duration. 16.101 kept a short arc and
 * delayed the fire, which left the ring sitting full while the module was still
 * locked out.
 */
export function tickSelfModuleCycle(
  state: SelfModuleCycleState,
  now: number,
  cycleDurationMs: number,
  cooldownMs = 0
): CycleResult<SelfModuleCycleState> {
  if (!state.active) {
    return { state, visual: "off", progress: 0, justFired: false };
  }
  // ONE interval, used for the schedule and for the arc alike (16.103, Don:
  // "just slow down the visual cycling of the module to match the new longer
  // time"). 16.101 kept a 4s arc and delayed the next fire, so the ring filled
  // in 4s and then sat at 100% for 12s -- the module looked ready and was not,
  // which is exactly the mismatch between what you see and what you feel.
  //
  // Note this also makes the FIRST cycle take the full interval. That is the
  // point: a module that cycles every 16s should cycle every 16s from the moment
  // it is switched on, not once quickly and then slowly.
  const interval = cycleDurationMs + cooldownMs;
  let next = state;
  if (next.nextTickAt === null) {
    next = { ...next, nextTickAt: now + interval };
  }
  if (now >= next.nextTickAt!) {
    if (next.deactivating) {
      // The grace cycle just completed -- honor the pending shutoff now
      // instead of rolling into another cycle.
      const off: SelfModuleCycleState = { targetId: null, active: false, nextTickAt: null, deactivating: false };
      return { state: off, visual: "off", progress: 0, justFired: true };
    }
    next = { ...next, nextTickAt: now + interval };
    return { state: next, visual: "cycling", progress: clamp01(1 - (next.nextTickAt! - now) / interval), justFired: true };
  }
  const visual = next.deactivating ? "deactivating" : "cycling";
  return { state: next, visual, progress: clamp01(1 - (next.nextTickAt! - now) / interval), justFired: false };
}

export function tapSelfModule(
  state: SelfModuleCycleState,
  now: number,
  cycleDurationMs: number,
  targetId: string,
  cooldownMs = 0
): SelfModuleCycleState {
  if (state.active) {
    if (state.deactivating) {
      // Re-tapped during the grace period -- cancel the pending shutoff
      // and keep running normally (Don: "can still turn it on at any
      // time if it's off or turning off").
      return { ...state, deactivating: false };
    }
    // Don't cut the current cycle short -- let it finish (grayed out via
    // the "deactivating" visual) before actually powering off.
    return { ...state, deactivating: true };
  }
  // Same interval the tick uses, or activation would schedule a short first
  // cycle and the arc would disagree with it on the very first press (16.103).
  return { targetId, active: true, nextTickAt: now + cycleDurationMs + cooldownMs, deactivating: false };
}

function clamp01(n: number): number {
  return Math.min(1, Math.max(0, n));
}
